import React from 'react';
import { Card, Typography, Empty, Row, Col, Badge, Space } from 'antd';
import {
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  FundOutlined,
  FileTextOutlined,
  TrophyOutlined,
  DashboardOutlined,
  ExportOutlined,
  ImportOutlined,
  DollarOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;

export type ReportType = 'lab' | 'pr&license' | 'import&export' | 'finance' | 'followup';

interface ReportItem {
  title: string;
  key: string;
  icon: React.ReactElement;
  description: string;
}

const reportTypesMap: Record<ReportType, ReportItem[]> = {
  lab: [
    {
      title: 'Earnings by Divisions',
      key: 'earnings-by-divisions',
      icon: <ExperimentOutlined />,
      description: 'Track laboratory transaction records',
    },
    {
      title: 'Test Count by Divisions',
      key: 'test-count-by-divisions',
      icon: <BarChartOutlined />,
      description: 'Comprehensive test analysis',
    },
    {
      title: 'Earnings by Divisions - Nominal Payments',
      key: 'earnings-by-divisions-nom-pay',
      icon: <TrophyOutlined />,
      description: 'Assignment distribution metrics',
    },
    {
      title: 'Test Count by Divisions - Nominal Payments',
      key: 'test-count-by-divisions-nom-pay',
      icon: <PieChartOutlined />,
      description: 'Status-based task breakdown',
    },
    {
      title: 'Sample Count by Divisions',
      key: 'sample-count-by-divisions',
      icon: <PieChartOutlined />,
      description: 'Status-based task breakdown',
    },
    {
      title: 'Item Quantity by Divisions',
      key: 'item-qty-by-divisions',
      icon: <PieChartOutlined />,
      description: 'Status-based task breakdown',
    },
  ],
  'pr&license': [
    {
      title: 'Earnings by Departments',
      key: 'pr-earnings-by-departments',
      icon: <FileTextOutlined />,
      description: 'Product registration overview',
    },
    {
      title: 'Registration Count by Departments',
      key: 'pr-count-by-departments',
      icon: <DashboardOutlined />,
      description: 'Company licensing analysis',
    },
    {
      title: 'Registrations by Establishment',
      key: 'registrations-by-establishment',
      icon: <DashboardOutlined />,
      description: 'All registration by Establishment',
    },
  ],
  'import&export': [
    {
      title: 'Earnings by Departments',
      key: 'imp-exp-earnings-by-departments',
      icon: <ImportOutlined />,
      description: 'Product import analytics',
    },
    {
      title: 'Import/Export Count by Departments',
      key: 'imp-exp-count-by-departments',
      icon: <LineChartOutlined />,
      description: 'Annual import value trends',
    },
    {
      title: 'Import vs Export Earnings Comparison',
      key: 'imp-exp-earnings-comparison',
      icon: <LineChartOutlined />,
      description: 'Annual import value trends',
    },
    {
      title: 'Import vs Export Count Comparison',
      key: 'imp-exp-counts-comparison',
      icon: <LineChartOutlined />,
      description: 'Annual import value trends',
    },
    {
      title: 'Import & Export Earnings by Departments for Nominal Payments',
      key: 'imp-exp-nominal-earnings',
      icon: <LineChartOutlined />,
      description: 'Annual import value trends',
    },
    {
      title: 'Import/Export Count by Department for Nominal Payments',
      key: 'imp-exp-nominal-count',
      icon: <LineChartOutlined />,
      description: 'Annual import value trends',
    },
    {
      title: 'L1, L2 Imports & Exports by Qty per Department',
      key: 'imp-exp-levels-by-qty',
      icon: <LineChartOutlined />,
      description: 'Annual import value trends',
    },
    {
      title: 'Consignment by Country',
      key: 'consignment-by-country',
      icon: <LineChartOutlined />,
      description: 'Annual import value trends',
    },
    {
      title: 'Tonnage Report',
      key: 'imp-exp-tonnage',
      icon: <LineChartOutlined />,
      description: 'Annual import value trends',
    },
  ],
  finance: [
    {
      title: 'H Form',
      key: 'h-form',
      icon: <DollarOutlined />,
      description: 'Financial revenue insights',
    },
    {
      title: 'Summary Report',
      key: 'summary-report',
      icon: <BarChartOutlined />,
      description: 'Department spending analysis',
    },
    {
      title: 'Earnings & Invoice Count Settled by by Cashier Point',
      key: 'cashier-point-summary',
      icon: <BarChartOutlined />,
      description: 'Department spending analysis',
    },
  ],
  followup: [
    {
      title: 'Revenue Summary',
      key: 'revenue-summary',
      icon: <DollarOutlined />,
      description: 'Financial revenue insights',
    },
    {
      title: 'Expenditure by Department',
      key: 'expenditure-department',
      icon: <BarChartOutlined />,
      description: 'Department spending analysis',
    },
  ],
};

interface ReportSelectorProps {
  repType: ReportType;
  onSelect?: (reportKey: string) => void;
}

const ReportSelector: React.FC<ReportSelectorProps> = ({ repType, onSelect }) => {
  const reports = reportTypesMap[repType] || [];

  if (reports.length === 0) return <Empty description="No reports available for this category." />;

  return (
    <div style={{ padding: '24px', background: '#ffffff', borderRadius: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          Reports Dashboard
        </Title>
      </div>

      <Row gutter={[16, 16]}>
        {reports.map((item) => (
          <Col xs={24} sm={12} md={12} lg={8} key={item.key}>
            <Card
              hoverable
              style={{
                borderRadius: '8px',
                border: '1px solid #e8e8e8',
                background: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#1890ff';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(24,144,255,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e8e8e8';
                e.currentTarget.style.boxShadow = 'none';
              }}
              onClick={() => onSelect?.(item.key)}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    background: '#f0f5ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <div style={{ fontSize: 20, color: '#8C1C03' }}>{item.icon}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
                      {item.title}
                    </Title>
                  </div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                    {item.description}
                  </Text>
                  {/* <div style={{ marginTop: 8 }}>
                    <Space size="small">
                      <Badge color="#1890ff" text="Analytics" />
                      <Badge color="#52c41a" text="Real-time" />
                    </Space>
                  </div> */}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default ReportSelector;
